import React from 'react';
import { createPortal } from 'react-dom';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import LearningContentDetailTable from '../export/LearningContentDetailTable';
import type {
  AnnualCrossTopicCountScope,
  AnnualCrossTopicProgramPlanGroup,
} from '../../utils/annualProgressCrossTopicExtract';
import {
  annualCrossTopicProgramPlanTitle,
  annualCrossTopicScopeLabel,
} from '../../utils/annualProgressCrossTopicExtract';
import {
  openDocumentExportPreviewFailureMessage,
  openDocumentExportPreviewInNewTab,
  openDocumentExportPreviewTabPlaceholder,
} from '../../utils/documentExportPreviewStorage';
import { saveAnnualCrossTopicProgramPlanAsExcel } from '../../utils/annualCrossTopicProgramPlanExcelExport';

export type AnnualCrossTopicProgramPlanModalProps = {
  open: boolean;
  onClose: () => void;
  scope: AnnualCrossTopicCountScope;
  groups: AnnualCrossTopicProgramPlanGroup[];
  loading?: boolean;
};

function sanitizeFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

const AnnualCrossTopicProgramPlanModal: React.FC<AnnualCrossTopicProgramPlanModalProps> = ({
  open,
  onClose,
  scope,
  groups,
  loading = false,
}) => {
  const printRootRef = React.useRef<HTMLDivElement | null>(null);
  const [exportBusy, setExportBusy] = React.useState(false);

  const scopeLabel = annualCrossTopicScopeLabel(scope);
  const documentTitle = annualCrossTopicProgramPlanTitle(scope);
  const canExport = !loading && groups.length > 0;

  const buildFileNameBase = React.useCallback(() => {
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    return `범교과운영계획_${sanitizeFileToken(scopeLabel)}_${dateLabel}`;
  }, [scopeLabel]);

  const openPreviewTab = React.useCallback(
    (action: 'pdf' | 'print') => {
      if (!canExport) return;
      const placeholder = openDocumentExportPreviewTabPlaceholder();
      const result = openDocumentExportPreviewInNewTab(
        {
          v: 1,
          kind: 'annual-cross-topic-program-plan',
          fileNameBase: buildFileNameBase(),
          printTitle: documentTitle,
          data: { scope, scopeLabel, documentTitle, groups },
        },
        action,
        { preOpenedWindow: placeholder }
      );
      if (!result.ok) {
        alert(openDocumentExportPreviewFailureMessage(result.reason));
      }
    },
    [buildFileNameBase, canExport, documentTitle, groups, scope, scopeLabel]
  );

  const handleSavePdf = React.useCallback(() => {
    openPreviewTab('pdf');
  }, [openPreviewTab]);

  const handleSaveExcel = React.useCallback(async () => {
    if (!canExport) return;
    setExportBusy(true);
    try {
      const fileName = `${buildFileNameBase()}.xlsx`;
      await saveAnnualCrossTopicProgramPlanAsExcel({
        groups,
        documentTitle,
        fileName,
      });
    } catch (error) {
      console.error('엑셀 저장 실패:', error);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildFileNameBase, canExport, documentTitle, groups]);

  const handlePrint = React.useCallback(() => {
    openPreviewTab('print');
  }, [openPreviewTab]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop cp-annual-cross-topic-detail-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="cp-modal cp-annual-cross-topic-detail-modal cp-annual-cross-topic-program-plan-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-annual-cross-topic-program-plan-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-annual-cross-topic-detail-header">
          <h3 id="cp-annual-cross-topic-program-plan-title">{documentTitle}</h3>
          <ModalDocumentActionButtons
            onSavePdf={() => void handleSavePdf()}
            onSaveExcel={() => void handleSaveExcel()}
            onPrint={() => void handlePrint()}
            onClose={onClose}
            saveDisabled={!canExport}
            saveExcelDisabled={!canExport}
            printDisabled={!canExport}
            busy={exportBusy}
          />
        </div>
        <div className="cp-modal-body cp-annual-cross-topic-detail-body cp-annual-cross-topic-program-plan-body">
          {loading ? (
            <p className="page-desc" role="status" aria-busy="true">
              데이터 불러오는 중…
            </p>
          ) : groups.length === 0 ? (
            <p className="page-desc">표시할 프로그램 내용이 없습니다.</p>
          ) : (
            <div ref={printRootRef} className="cp-annual-cross-topic-detail-print-root cp-annual-cross-topic-program-plan-print-root">
              <div className="cp-annual-cross-topic-program-plan-doc-title">
                <span className={`cp-annual-cross-topic-detail-scope cp-annual-cross-topic-detail-scope--${scope}`}>
                  {scopeLabel}
                </span>
                <span className="cp-annual-cross-topic-program-plan-doc-title-text">{documentTitle}</span>
              </div>
              {groups.map((group) => (
                <section key={group.topic} className="cp-annual-cross-topic-program-plan-group">
                  <div className="cp-annual-cross-topic-detail-subtitle cp-annual-cross-topic-program-plan-group-topic">
                    <span className="cp-annual-cross-topic-detail-topic" title={group.topic}>
                      {group.topic}
                    </span>
                  </div>
                  <div className="cp-annual-cross-topic-detail-table-wrap">
                    <LearningContentDetailTable rows={group.rows} />
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnualCrossTopicProgramPlanModal;
